import { useCallback, useEffect, useState } from "react";
import * as L from 'leaflet';
import { defaultMapConfig, useMapConfig } from "../../../../../SubjectsMap/helper";
import { OpenStreetMapProvider } from "leaflet-geosearch";

const useMapHelper = ({
  container,
  value,
  canEdit,
  onChange,
  country = '',
}) => {
  const [mapRef, setMapRef] = useState();
  const [polygon, setPolygon] = useState();
  const mapProvider = new OpenStreetMapProvider();
  const { TILE_LAYER_URL, MAP_TOKEN } = useMapConfig();

  const searchForLocation = (map) => {
    let zoom = map.getZoom() ? map.getZoom() : 11;
    if (country && country !== '') {
      mapProvider.search({ query: country }).then((results) => {
        let checkMyLoc = true;
        if (results && results.length > 0) {
          const result = results.find(r => r.raw && r.raw.lat && r.raw.lon);
          if (result) {
            checkMyLoc = false
            map.setView([result.raw.lat, result.raw.lon], zoom);
          }
        }
        if (checkMyLoc) {
          map.locate({ setView: true, watch: true, maxZoom: 18 });
          map.on('locationfound', (ev) => {
            map.setView([ev.latlng.lat, ev.latlng.lng], zoom);
          })
        }
      });
    } else {
      map.locate({ setView: true, watch: true, maxZoom: 18 });
      map.on('locationfound', (ev) => {
        map.setView([ev.latlng.lat, ev.latlng.lng], zoom);
      })
    }
  };

  // Map control handlers
  const zoomHandler = useCallback(() => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() + 1);
    }
  }, [mapRef]);

  const zoomOutHandler = useCallback(() => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() - 1)
    }
  }, [mapRef]);

  const moveUpHandler = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      const value = 30 / mapRef.getZoom();
      mapRef.panTo(new L.LatLng(center.lat + value, center.lng));
    }
  }, [mapRef]);

  const moveDownHandler = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      const value = 30 / mapRef.getZoom();
      mapRef.panTo(new L.LatLng(center.lat - value, center.lng));
    }
  }, [mapRef]);

  const createInstance = () => {
    if (container.current) {
      const c = L.DomUtil.get(document.getElementById(container.current));

      if (c != null) {
        c._leaflet_id = null;
      }

      const map = L.map(container.current, {
        ...defaultMapConfig,
        center: [0, 0],
        zoom: 5,
        editable: true,
        zoomSnap: 0,
      });

      L.control.scale().addTo(map);
      if (!value) {
        searchForLocation(map);
      }
      L.tileLayer(TILE_LAYER_URL, { access_token: MAP_TOKEN }).addTo(map);
      return map;
    }
  };

  const removeMarker = useCallback((id) => {
    if (mapRef) {
      mapRef.eachLayer((layer) => {
        if (id === layer._leaflet_id) {
          mapRef.removeLayer(layer);
        }
      })
    }
  }, [mapRef]);

  useEffect(() => {
    if (!mapRef) {
      setMapRef(createInstance());
    }
  }, []);

  const onSave = useCallback(() => {
    if (polygon) {
      const { _latlngs, _leaflet_id } = polygon;
      if (_leaflet_id && _latlngs) {
        removeMarker(_leaflet_id);
        onChange((_latlngs ? _latlngs[0] : []).map((v) => [v.lat, v.lng]));
      }
    }
  }, [polygon]);

  useEffect(() => {
    if (mapRef && canEdit) {
      mapRef.on('editable:drawing:end', (e) => {
        const { _latlngs, _leaflet_id } = e.layer;
        removeMarker(_leaflet_id);
        onChange((_latlngs ? _latlngs[0] : []).map((v) => [v.lat, v.lng]));
      });
    }
    if (!value && mapRef && !polygon && canEdit) {
      mapRef.editTools.startPolygon();
    }
  }, [mapRef]);

  useEffect(() => {
    if (mapRef && polygon && canEdit) {
      mapRef.on('mouseup', onSave);
    }

    return () => {
      if (mapRef && polygon && canEdit) {
        mapRef.off('mouseup', onSave);
      }
    }
  }, [mapRef, polygon]);

  useEffect(() => {
    if (mapRef && value && value.length) {
      if (!polygon) {
        const p = L.polygon(value, { color: '#00809E' })
          .addTo(mapRef);
        setPolygon(p);
        if (canEdit) {
          p.enableEdit();
        }
      } else {
        removeMarker(polygon._leaflet_id);
        const p = L.polygon(value, { color: '#00809E' })
          .addTo(mapRef);
        if (canEdit) {
          p.enableEdit();
        }
        setPolygon(p)
      }
    }
  }, [mapRef, value]);

  useEffect(() => {
    if (value && mapRef) {
      mapRef.fitBounds(value, { padding: [20, 20], duration: 0 });
      if (mapRef.options.zoomAnimation) {
        mapRef.options.zoomAnimation = true;
      }
    }
  }, [mapRef]);

  const clear = useCallback(() => {
    if (mapRef) {
      mapRef.editTools.startPolygon();
      onChange(null);
      removeMarker(polygon._leaflet_id);
      setPolygon(undefined)
    }
  }, [mapRef, polygon]);

  return {
    zoomHandler,
    zoomOutHandler,
    moveDownHandler,
    moveUpHandler,
    clear,
    mapRef,
  };
};

export default useMapHelper;
